import * as React from "react";
import { DataLayer } from "@grandvisionhq/analytics";
import { useBloomreachAppointmentContent } from "@grandvisionhq/bloomreach";
import { useIntl } from "@grandvisionhq/state";
import { useTrackPageView } from "@grandvisionhq/svc-tealium/events";
import { bookAppointmentWhatStepScreenEvent } from "../../../../../data-layer/v3/data-layer/actions.js";
import { useAppointmentContext } from "../../../../../pages/BookAppointment/context/v3/pages/BookAppointment/context/AppointmentContext.js";
import { useRoutingContext } from "../../../../../pages/BookAppointment/context/RoutingContext/v3/pages/BookAppointment/context/RoutingContext/context.js";
import AppointmentTypes from "./AppointmentTypes.js";
import useGetAgeGroupsSelectorAndSelectedAgeGroup from "./useGetAgeGroupsSelectorAndSelectedAgeGroup.js";
import useGetAppointmentTypesForSelectedAgeGroup from "./useGetAppointmentTypesForSelectedAgeGroup.js";
import useGetAvailableAgeGroups from "./useGetAvailableAgeGroups.js";
import { fromArrayToString } from "../../../../../utils/v3/utils/from-array-to-string.js";
import useBookAppointmentStoreById from "../../../../../pages/BookAppointment/hooks/api/v3/pages/BookAppointment/hooks/api/use-book-appointment-store-by-id.js";
import { useAppointmentTypes } from "../../../../../pages/BookAppointment/hooks/v3/pages/BookAppointment/hooks/use-appointment-types.js";
const AppointmentTypesContainer = () => {
  const { getLabel, getFormattedLabel } = useIntl();
  const [{ goTo }, { queryParams }] = useRoutingContext();
  const [{ setStore, setType }, { store }] = useAppointmentContext();
  const { appointmentDisplayType } = useBloomreachAppointmentContent();
  const filterWithGroupId = fromArrayToString(queryParams.groupId);
  const [getBookAppointmentStore, getBookAppointmentStoreQuery] = useBookAppointmentStoreById({
    isTryInStoreFlowEnabled: fromArrayToString(queryParams.tryInStoreFlow) === "1" ? true : false,
    isGroupIdEnabled: fromArrayToString(queryParams.groupId) === filterWithGroupId ? filterWithGroupId : null
  });
  const { sortedAppointmentTypes, groupedAppointmentTypes } = useAppointmentTypes();
  const [activeToolTip, setActiveToolTip] = React.useState(null);
  const labels = React.useMemo(
    () => ({
      heading: getLabel("appointments.component.appointmentTypes.heading"),
      description: getFormattedLabel("appointments.component.appointmentTypes.description", {
        hideLabelKey: true,
        parseLink: true,
        openLinksInNewTab: true
      }),
      alert: getFormattedLabel("appointments.component.appointmentTypes.noAppointmentsAlert", {
        hideLabelKey: true,
        parseLink: true
      })
    }),
    [getLabel, getFormattedLabel]
  );
  const onClick = React.useCallback(
    (type) => {
      if (!store) {
        return;
      }
      setType(type);
      const typeId = type.id;
      const storeId = `${store.globalStoreId}`;
      const { search, userLocation, linkedAppointmentTypeId } = queryParams;
      goTo("when", { typeId, storeId, search, userLocation, linkedAppointmentTypeId });
    },
    [queryParams, setType, goTo, store]
  );
  const onClickToolTip = React.useCallback((toolTip) => {
    setActiveToolTip(toolTip);
  }, []);
  const onCloseToolTip = React.useCallback(() => {
    setActiveToolTip(null);
  }, []);
  const aabWhatPageView = React.useCallback(
    () => new Promise((resolve) => {
      if (!store) {
        return;
      }
      resolve({
        id: "VirtualPage-View",
        Page_Type: "Info",
        Page_Section1: "Exam",
        Page_Section2: "Question1",
        Store_Search_StoreId: store.globalStoreId
      });
    }),
    [store]
  );
  useTrackPageView(aabWhatPageView);
  React.useEffect(() => {
    if (!store) {
      return;
    }
    const { storeId } = queryParams;
    const { appointmentTypes: appointmentTypes2 } = store;
    if (storeId === store.globalStoreId && appointmentTypes2.length) {
      const dataLayerEvent = bookAppointmentWhatStepScreenEvent({
        store: {
          code: store.code,
          // TODO: MACC-19077
          globalStoreId: store.globalStoreId,
          streetName: store.streetName,
          town: store.town,
          name: store.name,
          postalCode: store.postalCode,
          streetNumber: store.streetNumber,
          additionalStreetInfo: store.additionalStreetInfo
        }
      });
      DataLayer.pushEvent(dataLayerEvent);
    }
  }, [queryParams, store]);
  React.useEffect(() => {
    const { storeId: storeIdFromUrl } = queryParams;
    const globalStoreId = fromArrayToString(storeIdFromUrl);
    if (!store) {
      if (globalStoreId) {
        getBookAppointmentStore(globalStoreId);
      }
      return;
    }
    if (globalStoreId && globalStoreId !== store.globalStoreId) {
      getBookAppointmentStore(globalStoreId);
      return;
    }
    if (store.appointmentTypes?.length || store.globalStoreId === void 0) {
      return;
    }
    getBookAppointmentStore(store.globalStoreId);
  }, [getBookAppointmentStore, store]);
  React.useEffect(() => {
    const { called, data } = getBookAppointmentStoreQuery;
    if (!called || !data) {
      return;
    }
    setStore(data);
  }, [setStore, getBookAppointmentStoreQuery]);
  const availableAgeGroups = useGetAvailableAgeGroups(sortedAppointmentTypes);
  const { AgeGroupSelector, selectedAgeGroup } = useGetAgeGroupsSelectorAndSelectedAgeGroup(availableAgeGroups);
  const appointmentTypes = useGetAppointmentTypesForSelectedAgeGroup(
    selectedAgeGroup,
    sortedAppointmentTypes
  );
  const loading = getBookAppointmentStoreQuery.loading;
  return /* @__PURE__ */ React.createElement(
    AppointmentTypes,
    {
      labels,
      appointmentDisplayType,
      AgeGroupSelector: appointmentDisplayType === "group" ? null : AgeGroupSelector,
      appointmentTypes,
      groupedAppointmentTypes,
      activeToolTip,
      onClick,
      onClickToolTip,
      onCloseToolTip,
      loading
    }
  );
};
export {
  AppointmentTypesContainer as default
};
//# sourceMappingURL=AppointmentTypesContainer.js.map
